import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Copy, Share2, Check, Loader2, Trash2, Lock } from 'lucide-react';
import axios from 'axios';
import { useApp } from '@/contexts/AppContext';
import { toast } from 'sonner';

export default function ShareSnapshotDialog({ open, onOpenChange }) {
  const { API } = useApp();
  const [tab, setTab] = useState('create');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [token, setToken] = useState(null);
  const [copied, setCopied] = useState(false);

  // History state
  const [items, setItems] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  const reload = async () => {
    setLoadingList(true);
    try {
      const r = await axios.get(`${API}/board-snapshots`, { params: { limit: 50 } });
      setItems(r.data.items);
    } finally { setLoadingList(false); }
  };

  useEffect(() => {
    if (open && tab === 'history') reload();
  }, [open, tab]); // eslint-disable-line

  const shareUrl = (t) => `${window.location.origin}/share/board/${t}`;

  const create = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/board-snapshots`, { note: note || null });
      setToken(r.data.token);
      toast.success('Snapshot created');
    } catch {
      toast.error('Could not create snapshot');
    } finally { setBusy(false); }
  };

  const copy = async (t) => {
    try {
      await navigator.clipboard.writeText(shareUrl(t));
      setCopied(t);
      toast.success('Link copied');
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Could not copy link');
    }
  };

  const revoke = async (t) => {
    await axios.delete(`${API}/board-snapshots/${t}`);
    toast.success('Snapshot revoked');
    reload();
  };

  const handle = (next) => {
    onOpenChange(next);
    if (!next) {
      setTimeout(() => {
        setNote(''); setToken(null); setCopied(false); setTab('create');
      }, 250);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handle}>
      <DialogContent data-testid="share-snapshot-dialog" className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold tracking-tight text-apex-ink flex items-center gap-2">
            <Share2 className="w-4 h-4 text-apex-lens" />
            Share Board Snapshot
          </DialogTitle>
          <DialogDescription className="text-sm text-apex-neutral">
            Generate or manage public, read-only links to this quarter's governance proof.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="mt-2">
          <TabsList className="w-full">
            <TabsTrigger value="create" className="flex-1" data-testid="snap-tab-create">Create</TabsTrigger>
            <TabsTrigger value="history" className="flex-1" data-testid="snap-tab-history">History</TabsTrigger>
          </TabsList>

          <TabsContent value="create" className="space-y-4 mt-4">
            {!token ? (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="snap-note" className="text-xs font-semibold uppercase tracking-wider text-apex-neutral">Optional note</Label>
                  <Textarea id="snap-note" data-testid="snap-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Q2 2026 board pre-read" rows={3} maxLength={500} />
                </div>
                <DialogFooter className="gap-2">
                  <Button variant="outline" onClick={() => handle(false)}>Cancel</Button>
                  <Button onClick={create} disabled={busy} data-testid="snap-create" className="bg-apex-lens hover:bg-apex-lens-dark text-white gap-1.5">
                    {busy ? <><Loader2 className="w-4 h-4 animate-spin" />Creating…</> : <><Share2 className="w-4 h-4" />Create snapshot</>}
                  </Button>
                </DialogFooter>
              </>
            ) : (
              <>
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-apex-success font-medium">
                  Snapshot created. Anyone with this link can view it.
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-apex-neutral">Share URL</Label>
                  <div className="flex gap-2">
                    <Input data-testid="snap-share-url" readOnly value={shareUrl(token)} className="font-mono text-xs" onFocus={(e) => e.target.select()} />
                    <Button onClick={() => copy(token)} data-testid="snap-copy" className="bg-apex-lens hover:bg-apex-lens-dark text-white shrink-0 gap-1.5">
                      {copied === token ? <><Check className="w-3.5 h-3.5" />Copied</> : <><Copy className="w-3.5 h-3.5" />Copy</>}
                    </Button>
                  </div>
                </div>
                <DialogFooter className="gap-2">
                  <Button variant="outline" onClick={async () => { await revoke(token); setToken(null); setNote(''); setTab('history'); }} data-testid="snap-revoke-just-created" className="text-apex-critical hover:bg-red-50 hover:text-apex-critical gap-1.5">
                    <Trash2 className="w-3.5 h-3.5" />
                    Revoke now
                  </Button>
                  <Button variant="outline" onClick={() => { setToken(null); setNote(''); setTab('history'); }}>Create another</Button>
                  <Button onClick={() => handle(false)}>Done</Button>
                </DialogFooter>
              </>
            )}
          </TabsContent>

          <TabsContent value="history" className="mt-4">
            {loadingList ? (
              <div className="text-xs text-apex-neutral inline-flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" />Loading…</div>
            ) : items.length === 0 ? (
              <div className="text-xs text-apex-neutral text-center py-6">No snapshots yet.</div>
            ) : (
              <div className="space-y-2 max-h-[340px] overflow-y-auto pr-1" data-testid="snap-history-list">
                {items.map((s) => (
                  <div key={s.token} className={`flex items-center justify-between gap-2 border border-slate-200 rounded-md p-3 ${s.revoked ? 'bg-slate-50 opacity-70' : 'bg-white'}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-mono text-apex-neutral">{s.token.slice(0, 8)}…</span>
                        {s.revoked && <span className="text-[9px] uppercase tracking-wider text-apex-critical font-semibold inline-flex items-center gap-0.5"><Lock className="w-2.5 h-2.5" />Revoked</span>}
                      </div>
                      <div className="text-[11px] text-apex-ink-2 truncate">{s.note || <span className="italic text-apex-neutral">no note</span>}</div>
                      <div className="text-[10px] font-mono text-apex-neutral">{new Date(s.created_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}</div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {!s.revoked && (
                        <>
                          <Button onClick={() => copy(s.token)} variant="ghost" size="sm" data-testid={`snap-copy-${s.token}`} className="text-xs gap-1">
                            {copied === s.token ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                          </Button>
                          <Button onClick={() => revoke(s.token)} variant="ghost" size="sm" data-testid={`snap-revoke-${s.token}`} className="text-apex-critical hover:bg-red-50 hover:text-apex-critical gap-1 text-xs">
                            <Trash2 className="w-3 h-3" />
                            Revoke
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
